/**
 * statusMap.ts — STATUS_MAP, STATUS_TABS, BRANCHES constants for orders.
 */

import { PRODUCT_CATEGORIES } from "@/features/products/constants";

/** Flat lookup (sub-product id → full Vietnamese name). Built once. */
const PRODUCT_NAME_BY_ID: Record<string, string> = (() => {
  const out: Record<string, string> = {};
  for (const cat of Object.values(PRODUCT_CATEGORIES)) {
    for (const sp of cat.subProducts) out[sp.id] = sp.name;
  }
  return out;
})();

/**
 * shortProductName — port từ frontend-bk utils/helpers.js. Rút gọn nhãn dài
 * cho hiển thị trong list/table dày: "Profile Biểu Tượng" → "PF Icon",
 * "Ảnh Thẻ (có make)" → "AT (có make)".
 */
export function shortProductName(name: string | null | undefined): string {
  if (!name) return "";
  return name
    .replace("Profile ", "PF ")
    .replace("Ảnh Thẻ ", "AT ")
    .replace("Gói DN ", "DN ")
    .replace("Chân Dung ", "CD ")
    .replace("Cơ Bản", "Basic")
    .replace("Cao Cấp", "Premium")
    .replace("Hiện Đại", "Modern")
    .replace("Cổ Điển", "Classic")
    .replace("Biểu Tượng", "Icon");
}

/**
 * productLabel — sub-product id → display label.
 * Default short form ("AT (có make)"); pass `{full: true}` for full name.
 */
export function productLabel(id: string | null | undefined, opts?: { full?: boolean }): string {
  if (!id) return "";
  const full = PRODUCT_NAME_BY_ID[id];
  if (!full) return id; // unknown / custom id → fallback raw
  return opts?.full ? full : shortProductName(full);
}

/** Human-readable labels for each order status. */
export const STATUS_MAP: Record<string, string> = {
  tentative: "Dự bị",
  // Hợp đồng báo giá chờ khách chốt (máy chủ ép lúc tạo, QĐ 0178).
  "giữ chỗ tạm": "Giữ chỗ tạm",
  booked: "Đã đặt lịch",
  contract: "Ký HĐ",
  shooting: "Đang chụp",
  editing: "Đang sửa ảnh",
  // "Sửa xong" từ 04/10/2026 (QĐ 0292; trước đó "Hoàn thành"): ảnh đã sửa xong,
  // chờ gửi khách — và không còn lẫn với "Khép đơn". Chỉ đổi NHÃN, mã giữ nguyên.
  completed: "Sửa xong",
  delivered: "Đã giao",
  cancelled: "Huỷ",
};

/**
 * Hai thẻ KHÉP ĐƠN (QĐ 0292). KHÔNG phải giá trị của `orders.status`: đơn đã
 * khép vẫn mang `delivered`, kèm một mốc khép do máy đóng. Đi xuống máy chủ bằng
 * `?closure=open|closed` — xem CLOSURE_TAB_PARAM.
 */
export const UNCLOSED_TAB = "unclosed";
export const CLOSED_TAB = "closed";

export const CLOSURE_TAB_PARAM: Record<string, "open" | "closed"> = {
  [UNCLOSED_TAB]: "open",
  [CLOSED_TAB]: "closed",
};

/** Ordered list of status tabs shown in OrdersPage filter bar.
 *  `tentative` (đơn dự bị) giữ chỗ lịch chụp, chờ Chốt (→ booked) hoặc Hủy —
 *  có tab riêng để lễ tân/sale dễ theo dõi hàng chờ chốt.
 *
 *  BA giá trị KHÔNG phải status — `chua_coc`, `no_phone`, `postponed` — đi
 *  xuống máy chủ bằng tham số riêng, không phải `?status=`. Thêm giá trị tổng
 *  hợp mới thì phải khai cả ở SYNTHETIC_STATUS_TABS bên dưới, nếu không
 *  OrdersListView gửi nó như một status và danh sách trả về rỗng. */
export const STATUS_TABS: Array<{ value: string; label: string }> = [
  { value: "all", label: "Tất cả" },
  // key `chua_coc` giữ nguyên (URL + API `chuaCoc`); nhãn đổi cho khớp
  // nghĩa thật: đơn chưa nhận được đồng nào — xem filters.ts.
  { value: "chua_coc", label: "Chưa thu tiền" },
  { value: "no_phone", label: "📵 Chưa có SĐT" },
  // Tạm hoãn KHÔNG phải một giá trị của `status` — lệnh hoãn xoá trắng ngày
  // chụp rồi đẩy đơn vào blob `settings.postponed_orders`, còn trạng thái giữ
  // nguyên (thường `booked`) — nên đơn hoãn nằm lẫn trong tab "Đã đặt lịch" mà
  // không có gì phân biệt; trước 03/09/2026 chỉ màn Lịch mới thấy được chúng.
  // Đứng cạnh hai tab tổng hợp trên vì cùng nghĩa "đơn cần để mắt", không phải
  // một chặng của quy trình. Mất ngày chụp cũng có nghĩa mọi bộ lọc theo ngày
  // đều loại chúng ra.
  { value: "postponed", label: "⏸ Tạm hoãn" },
  // Thiếu ngày chụp cũng KHÔNG phải một giá trị của `status` — cùng họ với ba
  // thẻ cắt ngang phía trên. Từ 03/09/2026 (quyết định 0093) đơn không có ngày
  // chụp thôi được tính vào báo cáo tháng, nên đây là đường DUY NHẤT tìm lại
  // chúng để điền ngày; không có thẻ này thì chúng nằm im vĩnh viễn. Đơn tạm
  // hoãn bị loại khỏi đây (xem summaryWhere bên BE): hoãn cũng mất ngày chụp
  // nhưng đó là chủ ý của người thật, không phải việc bỏ sót.
  { value: "missing_shoot_date", label: "📅 Thiếu ngày chụp" },
  // Chụp bù cũng cắt ngang trạng thái: đơn bù 0đ sinh từ phiếu khiếu nại đi
  // đủ các chặng như đơn thường. Máy chủ lọc theo dây nối về đơn gốc
  // (`?reshoot=1`), không theo nhãn "🔁 Chụp lại" — nhãn gỡ tay được.
  { value: "reshoot", label: "🔁 Chụp bù" },
  { value: "tentative", label: STATUS_MAP["tentative"] ?? "Dự bị" },
  { value: "booked", label: STATUS_MAP["booked"] ?? "Đã đặt lịch" },
  { value: "contract", label: STATUS_MAP["contract"] ?? "Ký HĐ" },
  { value: "shooting", label: STATUS_MAP["shooting"] ?? "Đang chụp" },
  { value: "editing", label: STATUS_MAP["editing"] ?? "Đang sửa ảnh" },
  { value: "completed", label: STATUS_MAP["completed"] ?? "Sửa xong" },
  { value: "delivered", label: STATUS_MAP["delivered"] ?? "Đã giao" },
  // Hai thẻ khép đơn đứng NGAY SAU "Đã giao" vì chúng chia đôi chính ô đó: đã
  // giao mà còn thiếu việc (khách nhận ảnh · thu đủ · hàng in) và đã xong hẳn.
  { value: UNCLOSED_TAB, label: "Chưa khép" },
  { value: CLOSED_TAB, label: "Đã khép" },
  { value: "cancelled", label: STATUS_MAP["cancelled"] ?? "Huỷ" },
];

/**
 * Tab KHÔNG ứng với một giá trị `orders.status` nào. Mỗi cái đi xuống máy chủ
 * bằng tham số riêng của nó (`chua_coc` lọc thêm ở máy khách, `no_phone` →
 * `?no_phone=1`, `postponed` → `?postponed=1`).
 *
 * Gom về một chỗ vì trước đó danh sách này nằm rải trong ba điều kiện `!==`
 * nối nhau ở OrdersListView — quên một chỗ là tab mới gửi `?status=postponed`,
 * máy chủ so khớp chính xác, danh sách rỗng và KHÔNG lỗi nào nổi lên.
 */
export const SYNTHETIC_STATUS_TABS = new Set([
  "all",
  "chua_coc",
  "no_phone",
  "postponed",
  "missing_shoot_date",
  "reshoot",
  UNCLOSED_TAB,
  CLOSED_TAB,
]);

/** Tab này có phải một status thật để gửi `?status=` xuống máy chủ không. */
export function isRealStatusTab(value: string): boolean {
  return !SYNTHETIC_STATUS_TABS.has(value);
}

/**
 * Tab nhóm sản phẩm sinh từ danh mục SỐNG, không ghi cứng.
 *
 * Bỏ hẳn tab "Khác" của bản cũ: nó gửi `category=other` xuống máy chủ, mà máy
 * chủ lọc bằng `category = 'other'` KHỚP CHÍNH XÁC — không đơn nào mang chữ
 * đó, nên tab luôn rỗng VÀ đơn của nhóm lạ (mọi dòng GNCA) không hiện ở tab
 * nào ngoài "Tất cả".
 *
 * Danh sách rỗng (danh mục chưa tải xong) vẫn trả về tab "Tất cả" — trả mảng
 * rỗng là hàng tab biến mất và người dùng mất đường về danh sách đầy đủ.
 */
export function categoryTabsFrom(
  lines: readonly { lineCode: string; lineName: string; brandCode: string }[],
  brandCode: string,
): Array<{ value: string; label: string }> {
  const seen = new Set<string>();
  const out: Array<{ value: string; label: string }> = [{ value: "all", label: "Tất cả" }];
  for (const l of lines) {
    if (brandCode && l.brandCode !== brandCode) continue;
    if (seen.has(l.lineCode)) continue;
    seen.add(l.lineCode);
    out.push({ value: l.lineCode, label: l.lineName });
  }
  return out;
}

/**
 * Tab nhóm GHI CỨNG của bản cũ. Màn Đơn hàng đã chuyển sang categoryTabsFrom;
 * ba nơi còn dùng (đóng ca lịch, khối nhóm sản phẩm của KPI) là chuyện khác,
 * đổi kèm ở đây là sửa ngoài phạm vi.
 */
export const CATEGORY_TABS: Array<{ value: string; label: string }> = [
  { value: "all", label: "Tất cả" },
  { value: "profile", label: "Profile" },
  { value: "idphoto", label: "Ảnh thẻ" },
  { value: "corporate", label: "Doanh nghiệp" },
  { value: "other", label: "Khác" },
];

/** Branch list used in filters and order forms. Mirrors frontend-bk constants.js BRANCHES. */
export const BRANCHES: Array<{
  value: string;
  label: string;
  shortLabel?: string;
  city?: "HCM" | "HN";
}> = [
  { value: "sg_32", label: "SG - ĐƯỜNG 3/2", shortLabel: "ĐƯỜNG 3/2", city: "HCM" },
  { value: "sg_lvs", label: "SG - LÊ VĂN SỸ", shortLabel: "LÊ VĂN SỸ", city: "HCM" },
  { value: "hn_01", label: "HN - CƠ SỞ 1", shortLabel: "CƠ SỞ 1", city: "HN" },
  { value: "hn_dl", label: "HN - NGUYỄN KHANG", shortLabel: "NGUYỄN KHANG - HÀ NỘI", city: "HN" },
  { value: "ext_hn", label: "NGOÀI STUDIO HÀ NỘI", shortLabel: "NGOÀI STUDIO HN", city: "HN" },
  { value: "ext_sg", label: "NGOÀI STUDIO SG", shortLabel: "NGOÀI STUDIO SG", city: "HCM" },
];

export const ALL_BRANCHES_VALUE = "all";
export const ALL_BRANCHES_LABEL = "Tất cả chi nhánh";
export const HANOI_BRANCHES_VALUE = "hn_all";
export const HANOI_BRANCHES_LABEL = "Tất cả chi nhánh Hà Nội";
export const SAIGON_BRANCHES_VALUE = "sg_all";
export const SAIGON_BRANCHES_LABEL = "Tất cả chi nhánh Sài Gòn";

/** Build (id → BRANCH entry) lookup once. */
const BRANCH_BY_ID = new Map(BRANCHES.map((b) => [b.value, b]));

/** branchLabel("sg_32") → "ĐƯỜNG 3/2". Falls back to raw id when unknown. */
export function branchLabel(id: string | null | undefined, opts?: { full?: boolean }): string {
  if (!id) return "";
  if (id === ALL_BRANCHES_VALUE) return ALL_BRANCHES_LABEL;
  if (id === HANOI_BRANCHES_VALUE) return HANOI_BRANCHES_LABEL;
  if (id === SAIGON_BRANCHES_VALUE) return SAIGON_BRANCHES_LABEL;
  const b = BRANCH_BY_ID.get(id);
  if (!b) return id;
  return opts?.full ? b.label : (b.shortLabel ?? b.label);
}

/**
 * cancelReasonOf — lý do huỷ ghi trong sổ trạng thái của đơn (kiểm toán
 * 05/09/2026, OD-UI-04). `statusLog` là JSON tự do từ máy chủ nên đọc phòng
 * thủ: lấy entry CUỐI có `to === "cancelled"` và trả `reason` nếu có chữ.
 */
export function cancelReasonOf(statusLog: unknown): string | null {
  if (!Array.isArray(statusLog)) return null;
  for (let i = statusLog.length - 1; i >= 0; i--) {
    const e = statusLog[i] as { to?: unknown; reason?: unknown } | null;
    if (e && typeof e === "object" && e.to === "cancelled") {
      const r = typeof e.reason === "string" ? e.reason.trim() : "";
      return r || null;
    }
  }
  return null;
}

/**
 * stepEntryOf — lần GẦN NHẤT đơn vào bước `status`: lúc nào, ai làm (thanh 5
 * bước ở chi tiết đơn, QĐ 0280 câu 5a). Bỏ dòng đổi lịch (`schedule_change`) —
 * dòng đó nằm chung sổ nhưng `to` là chuỗi lịch, không phải trạng thái.
 */
export function stepEntryOf(statusLog: unknown, status: string): { at: string; by: string } | null {
  if (!Array.isArray(statusLog)) return null;
  for (let i = statusLog.length - 1; i >= 0; i--) {
    const e = statusLog[i] as { to?: unknown; at?: unknown; by?: unknown; type?: unknown } | null;
    if (!e || typeof e !== "object" || e.to !== status || e.type === "schedule_change") continue;
    if (typeof e.at !== "string" || !e.at) return null;
    return { at: e.at, by: typeof e.by === "string" ? e.by.trim() : "" };
  }
  return null;
}
